import React, { useState, useEffect } from 'react';

import '../../styles/AdminMenu.css';

import { AdminQuestion } from './AdminQuestion';
import { ChevronLeft } from './Icons/ChevronLeft';
import { ChevronDown } from './Icons/ChevronDown';
import { TrashIcon } from './Icons/TrashIcon';
import { CreateIcon } from './Icons/CreateIcon';

import { getQuestion } from '../../data-sources/api';

/**
 * AdminDropdown component manages and displays a dropdown menu with questions.
 * 
 * @param {string} itemText - The text to display for the dropdown item.
 * @param {number} shownDropdownId - The ID of the currently shown dropdown.
 * @param {function} handleDropdownSelection - Function to handle dropdown selection.
 * @param {number} itemID - The ID of the dropdown item.
 * @param {function} onCreate - Function to handle the creation of a new item.
 * @param {function} onDelete - Function to handle the deletion of an item.
 * @param {function} onAnswerChange - Function to handle answer changes.
 * @param {function} onQuestionDelete - Function to handle the deletion of a question.
 * @returns {JSX.Element} A component that renders a dropdown menu with questions.
 */
export function AdminDropdown({ itemText, shownDropdownId, handleDropdownSelection, itemID, onCreate, onDelete, onAnswerChange, onQuestionDelete }) {
    const [dropdownQuestions, setDropdownQuestions] = useState([]);

    /**
     * Handles the creation of a new item.
     */
    const createItem = () => {
        onCreate(itemID);
    };

    /**
     * Handles the deletion of an item.
     */
    const deleteItem = () => {
        onDelete(itemID);
    };

    /**
     * Fetches the list of questions based on the submenu ID.
     */
    const fetchQuestions = async (subMenuId) => {
        try {
            const response = await getQuestion(subMenuId);
            setDropdownQuestions(response.data)

        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    /**
     * Fetch questions that belong to the respective dropdown menu when the item ID changes
     */
    useEffect(() => {
        fetchQuestions(itemID);
    }, [itemID]);

    const isShowing = shownDropdownId === itemID;

    return (
        <div className={`dropdown-container ${isShowing && 'dropdown-container-showing'}`}>
            <div className="dropdown-head lnu-background" onClick={() => handleDropdownSelection(itemID)}>
                <div className="dropdown-head-text">
                    {isShowing ? <ChevronDown /> : <ChevronLeft />}
                    {itemText}
                </div>
                <div>
                    <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={createItem}
                        title="Skapa en fråga till dropdownmenyn"
                    >
                        <CreateIcon />
                    </button>
                    <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={() => {
                            if (window.confirm("Är du säker på att du vill ta bort denna dropdown och alla frågor under denna?")) deleteItem()
                        }}
                        title="Ta bort dropdownmeny"
                    >
                        <TrashIcon />
                    </button>
                </div>
            </div>
            {isShowing &&
                <div className="dropdown-questions">
                    {dropdownQuestions.map((props) => (
                        <AdminQuestion
                            key={props.id}
                            question={props.textUrl}
                            itemID={props.id}
                            audio={props.audioUrl}
                            video={props.videoUrl}
                            onAnswerChange={onAnswerChange}
                            onDelete={onQuestionDelete}
                        />
                    ))}
                </div>
            }
        </div>
    );
}
